import { ACTIVE_BRAND, BrandKey } from './branding';

/**
 * Facts about the backend this portal talks to, as opposed to the brand it
 * wears. They travel together today — every TVOICE host is served by the older
 * backend — but they are different things and are resolved separately so they
 * can diverge without a rewrite.
 */

export type CallDurationUnit = 'seconds' | 'milliseconds';

/**
 * What `call.duration` means on each backend.
 *
 * The switches store call duration in MILLISECONDS (duration=1000 for a one
 * second call, verified against answer_time/end_time). cpass converts it at the
 * boundary — see secondsOrNull() in FederatedCallQueryService — so its API
 * hands the browser real seconds. The older backend does no such conversion and
 * passes the raw switch value straight through, which is why the version-1.0
 * frontend divided by 1000 itself and was right to.
 *
 * One field, two units, decided by which backend answered. Reading it wrongly
 * is not a rounding error: it is off by a factor of a thousand.
 */
const UNIT_BY_PORTAL: Record<BrandKey, CallDurationUnit> = {
  gg: 'seconds',
  tvoice: 'milliseconds',
  // CatchMyCall is served by cpass, the same backend as gg — and this map keys
  // on WHICH BACKEND ANSWERED, not on who the customer is. cpass converts at the
  // boundary, so the browser already has seconds. Copying tvoice's value here
  // because both are "not gg" would make every CatchMyCall duration 1000x wrong.
  cmc: 'seconds',
};

/**
 * Deliberately NOT inferred from the value's magnitude. The ranges overlap —
 * a 15-second call in milliseconds is 15000, and a four-hour call in seconds is
 * 14400 — so a magnitude test silently mangles exactly the long calls someone
 * would be looking at. It has to be declared.
 */
export const resolveCallDurationUnit = (
  portal: BrandKey,
  override?: string,
): CallDurationUnit => {
  const wanted = (override || '').trim().toLowerCase();
  if (wanted === 'seconds' || wanted === 'milliseconds') return wanted;
  return UNIT_BY_PORTAL[portal] ?? 'seconds';
};

export const CALL_DURATION_UNIT: CallDurationUnit = resolveCallDurationUnit(
  ACTIVE_BRAND,
  process.env.REACT_APP_CALL_DURATION_UNIT,
);
